<template>
    <section class="ai-chat" aria-live="polite">
        <div ref="history" class="ai-chat__history" @scroll="onHistoryScroll">
            <div v-if="!messages.length" class="ai-chat__empty">
                <span class="ai-chat__empty-icon"><i class="bi-stars" aria-hidden="true"></i></span>
                <strong>Como posso ajudar?</strong>
                <p>Converse, consulte informações do sistema ou peça ajuda com uma atividade.</p>
            </div>
            <template v-for="{ key, item, messageSources } in renderedMessages" :key="key">
                <article class="ai-chat__row" :class="`ai-chat__row--${item.role}`">
                    <span class="ai-chat__avatar" aria-hidden="true">
                        <img v-if="item.role === 'user' && fotoUsuario" :src="fotoUsuario" alt=""
                            @error="fotoUsuario = ''" />
                        <i v-else :class="item.role === 'user' ? 'bi-person-fill' : 'bi-stars'"></i>
                    </span>
                    <div class="ai-chat__message" :class="`ai-chat__message--${item.role}`">
                        <p v-if="item.role === 'user'">{{ item.message }}</p>
                        <div v-else class="ai-chat__markdown" v-html="renderMarkdown(item.message)"></div>
                        <ul v-if="item.attachments?.length" class="ai-chat__message-files">
                            <li v-for="attachment in item.attachments" :key="attachment.id || attachment.name">
                                <i class="bi-file-earmark-pdf" aria-hidden="true"></i>
                                <span>{{ attachment.name }}</span>
                                <small>{{ formatFileSize(attachment.size) }}</small>
                            </li>
                        </ul>
                        <small v-if="item.status === 'sending'" class="ai-chat__status">
                            <i class="bi-clock" aria-hidden="true"></i> Enviando
                        </small>
                        <small v-else-if="item.status === 'processing'" class="ai-chat__status">
                            <i class="bi-arrow-repeat ai-chat__activity-icon" aria-hidden="true"></i> Processando...
                        </small>
                        <small v-else-if="item.status === 'error'" class="ai-chat__status ai-chat__status--error">
                            <i class="bi-exclamation-circle" aria-hidden="true"></i> Não enviada
                        </small>
                    </div>
                </article>
                <AssistantSources v-if="messageSources.length" :sources="messageSources" />
            </template>
            <AssistantSources v-if="!activity && !hasMessageSources" :sources="sources" />
            <article v-if="activity" class="ai-chat__row ai-chat__row--assistant ai-chat__activity">
                <span class="ai-chat__avatar" aria-hidden="true"><i class="bi-stars"></i></span>
                <div class="ai-chat__message ai-chat__message--assistant">
                    <p><i class="bi-arrow-repeat ai-chat__activity-icon" aria-hidden="true"></i> {{ activity.label }}</p>
                    <small v-if="activity.detail">{{ activity.detail }}</small>
                </div>
            </article>
            <article v-else-if="processing" class="ai-chat__row ai-chat__row--assistant">
                <span class="ai-chat__avatar" aria-hidden="true"><i class="bi-stars"></i></span>
                <div class="ai-chat__typing" aria-label="Assistente analisando">
                    <span></span><span></span><span></span>
                </div>
            </article>
            <button v-if="showNewMessagesHint" type="button" class="ai-chat__jump"
                @click="maybeScrollToEnd(true)">
                Novas mensagens <span aria-hidden="true">↓</span>
            </button>
        </div>
        <section v-if="queuedMessages.length" class="ai-chat__queue" aria-label="Mensagens na fila">
            <header>
                <strong>Fila</strong>
                <small>{{ queuedMessages.length }}</small>
            </header>
            <ol>
                <li v-for="item in queuedMessages" :key="item.serverId || item.clientId">
                    <span>{{ summarize(item.message) }}</span>
                    <small>Na fila</small>
                </li>
            </ol>
        </section>
        <form class="ai-chat__form" @submit.prevent="submit">
            <div v-if="attachments.length" class="ai-chat__selected-files">
                <span v-for="(file, index) in attachments" :key="`${file.name}-${file.size}`">
                    <i class="bi-file-earmark-pdf" aria-hidden="true"></i>
                    <b>{{ file.name }}</b>
                    <small>{{ formatFileSize(file.size) }}</small>
                    <button type="button" :disabled="locked" :aria-label="`Remover ${file.name}`"
                        @click="removeAttachment(index)">
                        <i class="bi-x" aria-hidden="true"></i>
                    </button>
                </span>
            </div>
            <small v-if="attachmentError" class="ai-chat__file-error">{{ attachmentError }}</small>
            <div class="ai-chat__composer">
                <input ref="fileInput" class="ai-chat__file-input" type="file" accept=".pdf,application/pdf"
                    multiple :disabled="locked" @change="selectAttachments">
                <button class="ai-chat__attach" type="button" :disabled="locked || attachments.length >= 3"
                    title="Anexar PDF" aria-label="Anexar PDF" @click="$refs.fileInput.click()">
                    <i class="bi-paperclip" aria-hidden="true"></i>
                </button>
                <textarea v-model="message" rows="3" :disabled="locked"
                    :placeholder="locked ? 'Aguarde a conclusão da análise…' : placeholder"
                    @keydown.enter.exact.prevent="submit"></textarea>
                <button class="ai-chat__send" type="submit"
                    :disabled="locked || (!message.trim() && !attachments.length)" title="Enviar mensagem"
                    aria-label="Enviar mensagem">
                    <i class="bi-arrow-up" aria-hidden="true"></i>
                </button>
            </div>
            <small>{{ locked ? 'O envio será liberado ao concluir o ciclo.' : 'Enter para enviar · Shift + Enter para nova linha' }}</small>
        </form>
    </section>
</template>

<script>
import { carregarFotoUsuarioLogado } from '@/utils/usuario-foto'
import { markdownToHtml } from '@/utils/markdownLite'
import { assistantMessageSources } from '@/utils/ai-assistant.messages'
import { isNearBottom, shouldAutoScroll } from '@/utils/chat-scroll'
import AssistantSources from './AssistantSources.vue'

export default {
    name: 'AssistantChat',
    components: { AssistantSources },
    emits: ['send'],
    props: {
        messages: { type: Array, default: () => [] },
        queuedMessages: { type: Array, default: () => [] },
        processing: Boolean,
        locked: Boolean,
        activity: { type: Object, default: null },
        sources: { type: Array, default: () => [] },
        placeholder: { type: String, default: 'Pergunte ou peça algo ao Thalamus…' },
    },
    data: () => ({
        message: '',
        attachments: [],
        attachmentError: '',
        fotoUsuario: '',
        pinnedToBottom: true,
        showNewMessagesHint: false,
    }),
    async created() {
        this.fotoUsuario = await carregarFotoUsuarioLogado()
    },
    computed: {
        renderedMessages() {
            return this.messages.map((item, index) => ({
                key: item.serverId || item.clientId || index,
                item,
                messageSources: assistantMessageSources(item),
            }))
        },
        hasMessageSources() {
            return this.renderedMessages.some((item) => item.messageSources.length)
        },
    },
    watch: {
        messages: {
            deep: true,
            handler() {
                this.maybeScrollToEnd()
            },
        },
        activity: {
            deep: true,
            handler() {
                this.maybeScrollToEnd()
            },
        },
        sources() {
            this.maybeScrollToEnd()
        },
    },
    methods: {
        onHistoryScroll() {
            this.pinnedToBottom = isNearBottom(this.$refs.history)
            if (this.pinnedToBottom) this.showNewMessagesHint = false
        },
        maybeScrollToEnd(force = false) {
            this.$nextTick(() => {
                const history = this.$refs.history
                if (!history) return
                if (!shouldAutoScroll(force, this.pinnedToBottom)) {
                    this.showNewMessagesHint = true
                    return
                }
                history.scrollTop = history.scrollHeight
                this.pinnedToBottom = true
                this.showNewMessagesHint = false
            })
        },
        submit() {
            const value = this.message.trim()
            if ((!value && !this.attachments.length) || this.locked) return
            this.$emit('send', { message: value, attachments: [...this.attachments] })
            this.message = ''
            this.attachments = []
            this.attachmentError = ''
            if (this.$refs.fileInput) this.$refs.fileInput.value = ''
            this.maybeScrollToEnd(true)
        },
        selectAttachments(event) {
            this.attachmentError = ''
            const selected = Array.from(event.target.files || [])
            const available = Math.max(0, 3 - this.attachments.length)
            for (const file of selected.slice(0, available)) {
                if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                    this.attachmentError = 'Somente arquivos PDF são aceitos.'
                    continue
                }
                if (file.size > 10 * 1024 * 1024) {
                    this.attachmentError = 'Cada PDF deve ter no máximo 10 MB.'
                    continue
                }
                const duplicate = this.attachments.some((item) =>
                    item.name === file.name && item.size === file.size)
                if (!duplicate) this.attachments.push(file)
            }
            if (selected.length > available) {
                this.attachmentError = 'Você pode anexar até 3 PDFs por mensagem.'
            }
            event.target.value = ''
        },
        removeAttachment(index) {
            this.attachments.splice(index, 1)
            this.attachmentError = ''
        },
        formatFileSize(value) {
            const size = Number(value)
            if (!Number.isFinite(size) || size <= 0) return ''
            if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`
            return `${(size / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
        },
        summarize(message) {
            const text = String(message || '').replace(/\s+/g, ' ').trim()
            return text.length > 110 ? `${text.slice(0, 107)}…` : text
        },
        renderMarkdown(message) {
            return markdownToHtml(message)
        },
    },
}
</script>

<style scoped>
.ai-chat { display: flex; flex: 1; min-height: 0; flex-direction: column; }
.ai-chat__history { position: relative; flex: 1; overflow: auto; padding: 1.25rem 1.15rem 0.75rem; scroll-behavior: smooth; }
.ai-chat__empty { display: grid; justify-items: center; max-width: 22rem; margin: 3rem auto; text-align: center; color: #475467; }
.ai-chat__empty-icon { display: grid; width: 3.5rem; height: 3.5rem; margin-bottom: 1rem; place-items: center; border-radius: 18px; background: linear-gradient(135deg, #edf2ff, #f4ebff); color: #6941c6; font-size: 1.45rem; box-shadow: 0 12px 28px rgba(105, 65, 198, 0.14); }
.ai-chat__empty strong { color: #101828; font-size: 1.05rem; }
.ai-chat__empty p { margin: 0.45rem 0 0; font-size: 0.9rem; line-height: 1.55; }
.ai-chat__row { display: flex; gap: 0.55rem; align-items: flex-end; margin-bottom: 1rem; }
.ai-chat__row--user { flex-direction: row-reverse; }
.ai-chat__avatar { display: grid; overflow: hidden; flex: 0 0 1.9rem; width: 1.9rem; height: 1.9rem; place-items: center; border: 1px solid #e4e7ec; border-radius: 50%; background: #fff; color: #6941c6; font-size: 0.8rem; box-shadow: 0 3px 10px rgba(16, 24, 40, 0.08); }
.ai-chat__avatar img { width: 100%; height: 100%; object-fit: cover; }
.ai-chat__row--user .ai-chat__avatar { border-color: #d6e4ff; background: #eef4ff; color: #3159b7; }
.ai-chat__message { max-width: 82%; padding: 0.78rem 0.92rem; border-radius: 17px; white-space: pre-wrap; line-height: 1.5; box-shadow: 0 4px 14px rgba(16, 24, 40, 0.06); }
.ai-chat__message p { margin: 0; }
.ai-chat__markdown :deep(p) { margin: 0 0 0.55rem; }
.ai-chat__markdown :deep(p:last-child) { margin-bottom: 0; }
.ai-chat__markdown :deep(ul), .ai-chat__markdown :deep(ol) { margin: 0.4rem 0; padding-left: 1.25rem; }
.ai-chat__markdown :deep(a) { color: #4f46e5; overflow-wrap: anywhere; }
.ai-chat__markdown :deep(table) { display: block; overflow-x: auto; max-width: 100%; margin: 0.45rem 0; border-collapse: collapse; font-size: 0.76rem; }
.ai-chat__markdown :deep(th), .ai-chat__markdown :deep(td) { padding: 0.35rem 0.45rem; border: 1px solid #e4e7ec; text-align: left; }
.ai-chat__message--user { border-bottom-right-radius: 5px; background: linear-gradient(135deg, #3159b7, #4f46e5); color: #fff; }
.ai-chat__message--assistant { border: 1px solid #eaecf0; border-bottom-left-radius: 5px; background: #fff; color: #1d2939; }
.ai-chat__message-files { display: grid; gap: 0.3rem; margin: 0.55rem 0 0; padding: 0; list-style: none; }
.ai-chat__message-files li { display: flex; gap: 0.35rem; align-items: center; min-width: 0; padding: 0.35rem 0.45rem; border-radius: 8px; background: rgba(255, 255, 255, 0.14); font-size: 0.72rem; }
.ai-chat__message-files span { overflow: hidden; flex: 1; text-overflow: ellipsis; white-space: nowrap; }
.ai-chat__message-files small { color: inherit; opacity: 0.72; white-space: nowrap; }
.ai-chat__activity .ai-chat__message { background: linear-gradient(135deg, #f8faff, #f5f3ff); color: #475467; }
.ai-chat__activity p { display: flex; gap: 0.45rem; align-items: center; font-size: 0.82rem; }
.ai-chat__activity small { display: block; margin-top: 0.3rem; color: #667085; font-size: 0.7rem; }
.ai-chat__activity-icon { color: #6941c6; animation: ai-activity-spin 1.2s linear infinite; }
.ai-chat__status { display: flex; gap: 0.3rem; align-items: center; margin-top: 0.35rem; color: rgba(255, 255, 255, 0.78); font-size: 0.7rem; }
.ai-chat__status--error { color: #fecaca; }
.ai-chat__typing { display: flex; gap: 0.28rem; padding: 0.85rem 1rem; border: 1px solid #eaecf0; border-radius: 17px 17px 17px 5px; background: #fff; }
.ai-chat__typing span { width: 0.42rem; height: 0.42rem; border-radius: 50%; background: #98a2b3; animation: typing 1.2s infinite ease-in-out; }
.ai-chat__typing span:nth-child(2) { animation-delay: 0.15s; }
.ai-chat__typing span:nth-child(3) { animation-delay: 0.3s; }
.ai-chat__jump { position: sticky; bottom: 0.35rem; display: block; margin: 0.4rem auto 0; padding: 0.35rem 0.65rem; border: 1px solid #c7d7fe; border-radius: 99px; background: #fff; color: #3159b7; box-shadow: 0 4px 12px rgba(49, 89, 183, 0.14); cursor: pointer; font-size: 0.72rem; font-weight: 700; }
.ai-chat__queue { margin: 0 1rem; padding: 0.65rem 0.75rem; border: 1px solid #dbe3f0; border-radius: 12px; background: #f8faff; }
.ai-chat__queue header { display: flex; gap: 0.4rem; align-items: center; color: #344054; font-size: 0.76rem; }
.ai-chat__queue header small { min-width: 1.2rem; padding: 0.05rem 0.3rem; border-radius: 99px; background: #e0e7ff; color: #4338ca; text-align: center; }
.ai-chat__queue ol { display: grid; gap: 0.32rem; margin: 0.45rem 0 0; padding-left: 1.25rem; }
.ai-chat__queue li { padding-left: 0.1rem; color: #475467; font-size: 0.72rem; }
.ai-chat__queue li span { display: inline; overflow-wrap: anywhere; }
.ai-chat__queue li small { margin-left: 0.4rem; color: #667085; white-space: nowrap; }
.ai-chat__form { padding: 0.8rem 1rem 1rem; border-top: 1px solid rgba(228, 231, 236, 0.85); background: rgba(255, 255, 255, 0.92); backdrop-filter: blur(12px); }
.ai-chat__selected-files { display: flex; gap: 0.4rem; flex-wrap: wrap; margin-bottom: 0.5rem; }
.ai-chat__selected-files > span { display: flex; gap: 0.3rem; align-items: center; min-width: 0; max-width: 100%; padding: 0.35rem 0.45rem; border: 1px solid #d0d5dd; border-radius: 9px; background: #fff; color: #344054; font-size: 0.7rem; }
.ai-chat__selected-files b { overflow: hidden; max-width: 13rem; text-overflow: ellipsis; white-space: nowrap; }
.ai-chat__selected-files small { color: #667085; white-space: nowrap; }
.ai-chat__selected-files button { display: grid; padding: 0; border: 0; background: transparent; color: #667085; cursor: pointer; }
.ai-chat__file-error { display: block; margin: 0 0 0.45rem; color: #b42318; font-size: 0.7rem; }
.ai-chat__composer { display: flex; gap: 0.6rem; align-items: flex-end; padding: 0.55rem; border: 1px solid #d0d5dd; border-radius: 18px; background: #fff; box-shadow: 0 5px 18px rgba(16, 24, 40, 0.08); transition: border-color 0.2s, box-shadow 0.2s; }
.ai-chat__composer:focus-within { border-color: #8098f9; box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1), 0 8px 22px rgba(16, 24, 40, 0.08); }
.ai-chat__file-input { display: none; }
.ai-chat__attach,
.ai-chat__send {
    display: grid;
    flex: 0 0 2.35rem;
    width: 2.35rem;
    min-width: 2.35rem;
    max-width: 2.35rem;
    height: 2.35rem;
    min-height: 2.35rem;
    padding: 0;
    place-items: center;
    border: 0;
    border-radius: 50%;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    overflow: hidden;
    aspect-ratio: 1 / 1;
}
.ai-chat__attach { background: #f2f4f7; color: #475467; }
.ai-chat__attach:disabled { cursor: default; opacity: 0.38; }
.ai-chat__form textarea { flex: 1; min-width: 0; max-height: 9rem; padding: 0.3rem 0.35rem; border: 0; outline: 0; resize: none; background: transparent; color: #101828; line-height: 1.45; }
.ai-chat__send { background: linear-gradient(135deg, #3159b7, #6941c6); color: #fff; box-shadow: 0 5px 12px rgba(79, 70, 229, 0.24); }
.ai-chat__send:disabled { cursor: default; opacity: 0.38; box-shadow: none; }
.ai-chat__form > small { display: block; margin: 0.45rem 0.25rem 0; color: #98a2b3; font-size: 0.7rem; }
@keyframes typing { 0%, 60%, 100% { transform: translateY(0); opacity: 0.45; } 30% { transform: translateY(-3px); opacity: 1; } }
@keyframes ai-activity-spin { to { transform: rotate(360deg); } }
</style>
